'use client';

import { CagrValues } from './types';
import { ToolsTranslations } from '@/lib/translations/types';

interface CalculatorInputsProps {
  values: CagrValues;
  onValuesChange: (field: keyof CagrValues, value: number | string) => void;
  translations: ToolsTranslations['cagr'];
}

export default function CalculatorInputs({ values, onValuesChange, translations }: CalculatorInputsProps) {
  const handleSliderChange = (field: keyof CagrValues, value: number) => {
    onValuesChange(field, value);
  };

  const handleInputChange = (field: keyof CagrValues, value: string) => {
    if (field === 'currency') {
      onValuesChange(field, value);
      return;
    }
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      onValuesChange(field, numValue);
    }
  };

  return (
    <div className="space-y-6">
      {/* Starting Amount */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <svg 
              className="w-4 h-4 text-blue-500 animate-pulse" 
              fill="currentColor" 
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path fillRule="evenodd" d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <label htmlFor="starting-amount-input" className="text-gray-700 font-medium">{translations.startingAmount}</label>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="starting-amount-input"
              type="text"
              value={values.startingAmount.toLocaleString('sv-SE')}
              onChange={(e) => handleInputChange('startingAmount', e.target.value.replace(/\s/g, ''))}
              className="w-24 text-right border-b-2 border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-0 px-1 py-1 text-gray-800 font-semibold bg-transparent"
              aria-label={translations.startingAmount}
            />
              <span className="text-gray-800 font-semibold">{values.currency}</span>
          </div>
        </div>
        <div className="relative">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.min((values.startingAmount / 1000000) * 100, 100)}%` }}
            ></div>
          </div>
          <input
            id="starting-amount-slider"
            type="range"
            min="100"
            max="1000000"
            step="100"
            value={values.startingAmount}
            onChange={(e) => handleSliderChange('startingAmount', parseInt(e.target.value))}
            className="absolute inset-0 w-full h-2 cursor-pointer slider"
            aria-label={`${translations.startingAmount} slider`}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>100 {values.currency}</span>
          <span>1 000 000 {values.currency}</span>
        </div>
      </div>

      {/* Ending Amount */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <svg 
              className="w-4 h-4 text-blue-500 animate-pulse" 
              fill="currentColor" 
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path fillRule="evenodd" d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <label htmlFor="ending-amount-input" className="text-gray-700 font-medium">{translations.endingAmount}</label>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="ending-amount-input"
              type="text"
              value={values.endingAmount.toLocaleString('sv-SE')}
              onChange={(e) => handleInputChange('endingAmount', e.target.value.replace(/\s/g, ''))}
              className="w-24 text-right border-b-2 border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-0 px-1 py-1 text-gray-800 font-semibold bg-transparent"
              aria-label={translations.endingAmount}
            />
              <span className="text-gray-800 font-semibold">{values.currency}</span>
          </div>
        </div>
        <div className="relative">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.min((values.endingAmount / 2000000) * 100, 100)}%` }}
            ></div>
          </div>
          <input
            id="ending-amount-slider"
            type="range"
            min="100"
            max="2000000"
            step="100"
            value={values.endingAmount}
            onChange={(e) => handleSliderChange('endingAmount', parseInt(e.target.value))}
            className="absolute inset-0 w-full h-2 cursor-pointer slider"
            aria-label={`${translations.endingAmount} slider`}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>100 {values.currency}</span>
          <span>2 000 000 {values.currency}</span>
        </div>
      </div>

      {/* Years */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <svg 
              className="w-4 h-4 text-blue-500 animate-pulse" 
              fill="currentColor" 
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path fillRule="evenodd" d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <label htmlFor="years-input" className="text-gray-700 font-medium">{translations.years}</label>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="years-input"
              type="number"
              value={values.years}
              onChange={(e) => handleInputChange('years', e.target.value)}
              className="w-20 text-right border-b-2 border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-0 px-1 py-1 text-gray-800 font-semibold bg-transparent"
              aria-label={translations.years}
            />
            <span className="text-gray-800 font-semibold">{translations.yearsSuffix}</span>
          </div>
        </div>
        <div className="relative">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(values.years / 50) * 100}%` }}
            ></div>
          </div>
          <input
            id="years-slider"
            type="range"
            min="1"
            max="50"
            step="1"
            value={values.years}
            onChange={(e) => handleSliderChange('years', parseInt(e.target.value))}
            className="absolute inset-0 w-full h-2 cursor-pointer slider"
            aria-label={`${translations.years} slider`}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>1 {translations.yearsSuffix}</span>
          <span>50 {translations.yearsSuffix}</span>
        </div>
      </div>

      

      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #10b981;
          cursor: pointer;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        
        .slider::-moz-range-thumb {
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #10b981;
          cursor: pointer;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
      `}</style>
    </div>
  );
}


